/**
 * @file useVideoData.js
 * @author Paola Bustos, Yevheniia Bazhmaieva
 * @created 2025-07-14
 * @lastModified 2025-07-29
 * @description Custom React hook for managing video data and chat messages from Firebase Firestore.
 *              Handles data fetching, state management, loading states, and error handling for archived videos.
 */

import { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";

/**
 * Custom hook for managing video data and associated chat messages
 *
 * Fetches video metadata and chat messages from Firebase Firestore when a video ID is provided.
 * Manages loading states, error handling, and data processing for video archive functionality.
 *
 * Features:
 * - Automatic data fetching when video ID changes
 * - State reset on video change to prevent data mixing
 * - Chat message sorting by timestamp
 * - Initial message filtering (first 10 seconds)
 * - Loading and error state management
 * - Debug logging for data verification
 *
 * @param {string|null} selectedVideoId - Firebase document ID of the selected video
 * @returns {Object} Hook return object containing video data and state management functions
 * @returns {Object|null} returns.metadata - Video metadata object from Firestore
 * @returns {Array} returns.chatData - Complete array of chat messages sorted by time
 * @returns {Array} returns.displayedMessages - Limited array of messages for UI display (last 50)
 * @returns {Array} returns.allShownMessages - All messages that should be visible at current time
 * @returns {boolean} returns.isLoading - Loading state indicator
 * @returns {string|null} returns.error - Error message if data fetching fails
 * @returns {Function} returns.setAllShownMessages - State setter for all shown messages
 * @returns {Function} returns.setDisplayedMessages - State setter for displayed messages
 */

const useVideoData = (selectedVideoId) => {
  // ==================== STATE MANAGEMENT ====================

  /** @type {Object|null} Video metadata including title, description, videoURL, etc. */
  const [metadata, setMetadata] = useState(null);

  /** @type {Array} Complete array of chat messages sorted by timestamp */
  const [chatData, setChatData] = useState([]);

  /** @type {Array} Limited array of messages for UI display (typically last 50 messages) */
  const [displayedMessages, setDisplayedMessages] = useState([]);

  /** @type {Array} All messages that should be visible based on current video time */
  const [allShownMessages, setAllShownMessages] = useState([]);

  /** @type {boolean} Loading state indicator for async operations */
  const [isLoading, setIsLoading] = useState(false);

  /** @type {string|null} Error message if data fetching or processing fails */
  const [error, setError] = useState(null);

  // ==================== DATA FETCHING EFFECT ====================

  /**
   * Effect hook that handles video data loading from Firebase
   * Triggers whenever selectedVideoId changes
   * Manages complete data fetching lifecycle including loading states and error handling
   */
  useEffect(() => {
    /**
     * Async function to load video metadata and chat messages from Firebase Firestore
     *
     * Process:
     * 1. Validates video ID presence
     * 2. Resets all states to prevent data mixing
     * 3. Fetches video metadata from 'videos' collection
     * 4. Fetches and sorts chat messages from subcollection
     * 5. Processes initial messages for display
     * 6. Handles errors and sets appropriate states
     *
     * @async
     * @function loadVideoDataFromFirebase
     */

    const loadVideoDataFromFirebase = async () => {
      // Early return if no video ID provided
      if (!selectedVideoId) {
        setError(null);
        setIsLoading(false);
        return;
      }

      console.log("Loading video data from Firebase for ID:", selectedVideoId);
      setIsLoading(true);
      setError(null);

      // Reset all states to prevent data mixing between videos
      setMetadata(null);
      setChatData([]);
      setDisplayedMessages([]);
      setAllShownMessages([]);

      try {
        // ==================== FETCH VIDEO METADATA ====================

        const videoDocRef = doc(db, "videos", selectedVideoId);
        const videoDocSnap = await getDoc(videoDocRef);

        if (videoDocSnap.exists()) {
          const videoData = videoDocSnap.data();
          setMetadata(videoData);
          console.log("Video URL:", videoData.videoURL);
          console.log("Metadata loaded:", videoData.title);

          // ==================== FETCH CHAT MESSAGES ====================

          const chatCollectionRef = collection(
            db,
            "videos",
            selectedVideoId,
            "chatMessages",
          );
          const q = query(chatCollectionRef, orderBy("time_in_seconds", "asc"));
          const chatSnapshot = await getDocs(q);
          const chatMessages = chatSnapshot.docs.map((doc) => doc.data());

          setChatData(chatMessages);
          console.log(
            `Chat loaded from Firestore: ${chatMessages.length} messages.`,
          );

          // ==================== DATA PROCESSING AND VALIDATION ====================

          // Debug: Verify chat data time range for validation
          if (chatMessages.length > 0) {
            const maxTime = Math.max(
              ...chatMessages.map((m) => m.time_in_seconds || 0),
            );
            const minTime = Math.min(
              ...chatMessages.map((m) => m.time_in_seconds || 0),
            );
            console.log(
              `Chat time range: ${minTime}s to ${maxTime}s (${(maxTime / 60).toFixed(1)} minutes)`,
            );
          }

          // Filter and set initial messages (first 10 seconds of video)
          const initialMessages = chatMessages.filter(
            (msg) => msg.time_in_seconds <= 10,
          );
          setAllShownMessages(initialMessages);
          setDisplayedMessages(initialMessages.slice(-50)); // Limit to last 50 for performance
        } else {
          // Handle case where video document doesn't exist
          setError(
            `Video with ID "${selectedVideoId}" not found in Firestore.`,
          );
        }
      } catch (err) {
        // Handle any errors during data fetching
        setError("Failed to load video data from Firebase.");
        console.error("Error loading video data from Firebase:", err);
      } finally {
        // Always set loading to false regardless of success/failure
        setIsLoading(false);
      }
    };

    // Only execute data loading if we have a valid video ID
    if (selectedVideoId) {
      loadVideoDataFromFirebase();
    }
  }, [selectedVideoId]); // Re-run effect when video ID changes

  // ==================== HOOK RETURN ====================

  /**
   * Return object containing all video data and state management functions
   * Provides both data and control functions for consuming components
   */
  return {
    // Data states
    metadata, // Video metadata object
    chatData, // Complete chat messages array
    displayedMessages, // Limited messages for UI display
    allShownMessages, // All visible messages at current time

    // Control states
    isLoading, // Loading indicator
    error, // Error message if any

    // State setters for external control
    setAllShownMessages, // Update all shown messages
    setDisplayedMessages, // Update displayed messages
  };
};

export default useVideoData;
